import { useState, useEffect, useCallback } from 'react';
import { Shuffle, Loader2, Trophy, Play, RefreshCw, Space, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getTeams, getGroups, performDraw, resetDraw, getDrawSequence, generateCalendar, getSettings } from '../../lib/api';
import { KnockoutDrawDialog } from '../../components/common/KnockoutDrawDialog';
import api from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Animated slot-machine style team reveal component
const TeamReveal = ({ team, group, index, isRevealing }) => {
  const [showTeam, setShowTeam] = useState(false);
  
  useEffect(() => {
    if (isRevealing) {
      setShowTeam(false);
      const timer = setTimeout(() => setShowTeam(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isRevealing, team]);
  
  const groupColors = {
    'A': 'from-blue-500 to-blue-700',
    'B': 'from-green-500 to-green-700', 
    'C': 'from-purple-500 to-purple-700',
  };
  
  return (
    <div 
      className={cn(
        "relative overflow-hidden rounded-xl transition-all duration-500",
        showTeam ? "opacity-100 transform translate-y-0" : "opacity-0 transform -translate-y-4"
      )}
    >
      <div className={cn(
        "absolute inset-0 bg-gradient-to-r opacity-20",
        groupColors[group]
      )} />
      <div className="relative flex items-center justify-between p-4 border border-slate-700/50 rounded-xl bg-slate-800/50 backdrop-blur">
        <div className="flex items-center gap-4">
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg bg-gradient-to-br",
            groupColors[group]
          )}>
            {index + 1}
          </div>
          <div>
            <p className="font-heading text-lg font-bold uppercase tracking-wide">{team}</p>
          </div>
        </div>
        <div className={cn(
          "px-4 py-2 rounded-lg font-heading font-bold text-white bg-gradient-to-r",
          groupColors[group]
        )}>
          GIRONE {group}
        </div>
      </div>
    </div>
  );
};

// Animated urna (ball container)
const DrawAnimation = ({ isDrawing }) => {
  return (
    <div className="relative w-48 h-48 mx-auto mb-8">
      <div className="absolute inset-0 rounded-full bg-gradient-to-b from-slate-700 to-slate-900 border-4 border-gold/50 shadow-2xl shadow-gold/20">
        <div className="absolute inset-4 rounded-full bg-slate-950/80 flex items-center justify-center">
          {isDrawing ? (
            <div className="animate-bounce">
              <Shuffle className="w-12 h-12 text-gold animate-spin" />
            </div>
          ) : (
            <Trophy className="w-12 h-12 text-gold" />
          )}
        </div>
      </div>
      {isDrawing && (
        <>
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-4 h-4 rounded-full bg-primary animate-ping"
              style={{
                top: `${20 + Math.random() * 60}%`,
                left: `${20 + Math.random() * 60}%`,
                animationDelay: `${i * 200}ms`,
                animationDuration: '1s'
              }}
            />
          ))}
        </>
      )}
    </div>
  );
};

export const AdminSorteggio = () => {
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [generatingCalendar, setGeneratingCalendar] = useState(false);
  
  // Animation state
  const [animating, setAnimating] = useState(false);
  const [drawSequence, setDrawSequence] = useState([]);
  const [visibleDraws, setVisibleDraws] = useState([]);
  const [waitingForSpace, setWaitingForSpace] = useState(false);
  const [showKnockoutDraw, setShowKnockoutDraw] = useState(false);
  const [knockoutPairing, setKnockoutPairing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [teamsRes, groupsRes, settingsRes] = await Promise.all([
        getTeams(),
        getGroups(),
        getSettings()
      ]);
      setTeams(teamsRes.data);
      setGroups(groupsRes.data);
      setSettings(settingsRes.data);
      const f = settingsRes.data?.knockout_draw_first_group;
      const s = settingsRes.data?.knockout_draw_second_group;
      if (f && s) setKnockoutPairing({ first: f, second: s });
      else setKnockoutPairing(null);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSaveKnockoutPairing = async ({ picked_first_group, picked_second_group }) => {
    try {
      await api.post('/knockout/draw-pairing', null, {
        params: { picked_first_group, picked_second_group },
      });
      toast.success(`Sorteggio quarti memorizzato: 1° ${picked_first_group} vs 2° ${picked_second_group}`);
      setShowKnockoutDraw(false);
      setKnockoutPairing({ first: picked_first_group, second: picked_second_group });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Spacebar handler for step-by-step draw
  useEffect(() => {
    if (!waitingForSpace) return;
    
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        
        setVisibleDraws(prev => {
          const nextIndex = prev.length;
          if (nextIndex < drawSequence.length) {
            // New extraction at TOP (most recent first)
            const newDraws = [drawSequence[nextIndex], ...prev];
            
            // If this was the last one, finish animation
            if (newDraws.length === drawSequence.length) {
              setTimeout(() => {
                setAnimating(false);
                setWaitingForSpace(false);
                toast.success('Sorteggio gironi completato! Ora sorteggia gli accoppiamenti dei quarti.');
                fetchData();
                setShowKnockoutDraw(true);
              }, 500);
            }
            
            return newDraws;
          }
          return prev;
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [waitingForSpace, drawSequence, fetchData]);

  const handleDraw = async () => {
    if (teams.length !== 12) {
      toast.error('Servono esattamente 12 squadre per il sorteggio');
      return;
    }

    setDrawing(true);
    try {
      const response = await performDraw();
      const sequence = response.data.draw_sequence;
      setDrawSequence(sequence);
      
      // Start step-by-step animation (spacebar controlled)
      setAnimating(true);
      setVisibleDraws([]);
      setWaitingForSpace(true);
      setDrawing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel sorteggio');
      setDrawing(false);
    }
  };

  const handleGenerateCalendar = async () => {
    setGeneratingCalendar(true);
    try {
      await generateCalendar();
      toast.success('Calendario generato con successo');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione del calendario');
    } finally {
      setGeneratingCalendar(false);
    }
  };

  const handleResetDraw = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare il sorteggio?\n\nQuesto rimuoverà:\n• I 3 gironi\n• Tutte le partite (girone + eliminatorie)\n• Il sorteggio degli accoppiamenti quarti\n• Le statistiche delle squadre\n\nLe squadre e i giocatori NON verranno toccati.')) return;
    try {
      await resetDraw();
      toast.success('Sorteggio annullato');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nell\'annullamento');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const groupColors = {
    'A': 'bg-blue-500/20 border-blue-500/50 text-blue-400',
    'B': 'bg-green-500/20 border-green-500/50 text-green-400',
    'C': 'bg-purple-500/20 border-purple-500/50 text-purple-400',
  };

  return (
    <div className="p-6" data-testid="admin-sorteggio">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">SORTEGGIO GIRONI</h1>
          <p className="text-slate-400">
            {settings?.draw_completed 
              ? 'Sorteggio completato' 
              : `${teams.length}/12 squadre pronte per il sorteggio`}
          </p>
        </div>

        {/* Action Buttons - Always visible */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-heading text-lg font-bold">
                  {settings?.draw_completed ? 'Rifai Sorteggio' : 'Avvia Sorteggio'}
                </h3>
                <p className="text-sm text-slate-400">
                  {settings?.draw_completed 
                    ? 'Rimescola le squadre nei gironi (cancella le partite esistenti)'
                    : `Le 12 squadre verranno distribuite nei 3 gironi`}
                </p>
              </div>
              <Button
                onClick={handleDraw}
                disabled={drawing || animating || teams.length !== 12}
                className={cn(
                  "px-8",
                  settings?.draw_completed 
                    ? "bg-slate-700 text-white hover:bg-slate-600"
                    : "bg-gold text-slate-950 hover:bg-gold/90"
                )}
                data-testid="start-draw-btn"
              >
                {drawing ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-5 h-5 mr-2" />
                )}
                {drawing ? 'Sorteggio in corso...' : settings?.draw_completed ? 'Rifai Sorteggio' : 'Esegui Sorteggio'}
              </Button>
            </div>
            {settings?.draw_completed && (
              <div className="mt-4 pt-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div>
                  <h4 className="font-heading text-sm font-bold text-red-400">Cancella Sorteggio</h4>
                  <p className="text-xs text-slate-400">
                    Annulla tutto: gironi, partite, eliminatorie, accoppiamenti quarti. Le squadre restano.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={handleResetDraw}
                  className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500"
                  data-testid="reset-draw-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Cancella Sorteggio
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Animation View - Spacebar controlled */}
        {animating && (
          <div className="mb-8">
            <Card className="bg-gradient-to-b from-slate-900 to-slate-950 border-2 border-gold/30 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(234,179,8,0.1),transparent_50%)]" />
              <CardHeader className="relative">
                <CardTitle className="font-heading text-2xl text-center">
                  <span className="text-gold">SORTEGGIO IN CORSO</span>
                </CardTitle>
                <p className="text-center text-sm text-slate-400 mt-2">
                  Premi <kbd className="px-2 py-1 bg-slate-700 rounded text-gold font-mono mx-1">SPAZIO</kbd> per estrarre la prossima squadra
                </p>
              </CardHeader>
              <CardContent className="relative">
                <DrawAnimation isDrawing={visibleDraws.length < drawSequence.length} />
                
                <div className="text-center mb-6">
                  <span className="font-numbers text-4xl font-bold text-primary">
                    {visibleDraws.length}
                  </span>
                  <span className="text-slate-500 text-2xl mx-2">/</span>
                  <span className="font-numbers text-2xl text-slate-400">
                    {drawSequence.length}
                  </span>
                  <p className="text-sm text-slate-500 mt-2">squadre estratte</p>
                </div>
                
                {visibleDraws.length < drawSequence.length && (
                  <div className="text-center mb-4 animate-pulse">
                    <span className="text-gold text-sm font-semibold">
                      Premi SPAZIO per la prossima estrazione...
                    </span>
                  </div>
                )}
                
                <div className="grid gap-3 max-h-[400px] overflow-y-auto pr-2">
                  {visibleDraws.map((draw, index) => (
                    <TeamReveal
                      key={draw.team_id || draw.team_name}
                      team={draw.team_name}
                      group={draw.group_name}
                      index={visibleDraws.length - 1 - index}
                      isRevealing={index === 0}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Groups Display */}
        {settings?.draw_completed && !animating && groups.length > 0 && (
          <>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {groups.map(group => (
                <Card key={group.id} className={cn("bg-slate-900 border-2", groupColors[group.name])}>
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Trophy className="w-5 h-5" />
                      GIRONE {group.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {(group.teams_data || []).map((team, idx) => (
                        <li 
                          key={team.id}
                          className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg"
                        >
                          <span className="font-numbers text-lg text-slate-500 w-6">{idx + 1}</span>
                          <span className="font-semibold">{team.name}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Generate Calendar */}
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-lg font-bold">Genera Calendario</h3>
                    <p className="text-sm text-slate-400">
                      Crea le partite dei gironi (Mar, Gio, Sab)
                    </p>
                  </div>
                  <Button
                    onClick={handleGenerateCalendar}
                    disabled={generatingCalendar}
                    className="bg-primary text-slate-950 hover:bg-primary/90 px-8"
                    data-testid="generate-calendar-btn"
                  >
                    {generatingCalendar ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Play className="w-5 h-5 mr-2" />
                    )}
                    Genera Calendario
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Knockout draw status / re-trigger */}
        {settings?.draw_completed && (
          <Card className="bg-slate-900 border-slate-800" data-testid="knockout-pairing-status">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5 text-gold" /> Sorteggio Quarti di Finale
              </CardTitle>
            </CardHeader>
            <CardContent>
              {knockoutPairing ? (
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Accoppiamento sorteggiato:</p>
                    <p className="font-heading text-xl">
                      <span className="text-primary">1° {knockoutPairing.first}</span>
                      <span className="text-slate-500 mx-2">vs</span>
                      <span className="text-gold">2° {knockoutPairing.second}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      Gli altri 3 accoppiamenti sono determinati automaticamente dalle classifiche dei gironi.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowKnockoutDraw(true)}
                    className="border-slate-700 hover:border-gold"
                    data-testid="redraw-knockout-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Rifai sorteggio
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <p className="text-sm text-slate-400">
                    Sorteggia subito quale 1° classificata sfiderà quale 2° classificata ai quarti.
                  </p>
                  <Button
                    onClick={() => setShowKnockoutDraw(true)}
                    className="bg-gold text-slate-950 hover:bg-gold/90"
                    data-testid="open-knockout-draw-btn"
                  >
                    <Shuffle className="w-4 h-4 mr-2" /> Sorteggia Quarti
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Teams waiting for draw */}
        {!settings?.draw_completed && !animating && teams.length > 0 && (
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Squadre in attesa del sorteggio</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {teams.map((team, idx) => (
                  <div 
                    key={team.id}
                    className="p-3 bg-slate-800/50 rounded-lg text-center"
                  >
                    <span className="font-numbers text-sm text-slate-500">{idx + 1}</span>
                    <p className="font-semibold truncate">{team.name}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <KnockoutDrawDialog
          open={showKnockoutDraw}
          onClose={() => setShowKnockoutDraw(false)}
          onConfirm={handleSaveKnockoutPairing}
        />
      </div>
    </div>
  );
};

export default AdminSorteggio;
